import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpHeaders } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { TranslocoService } from '@ngneat/transloco';
import { AccessBlockService } from '../service/access-block.service';
import { AuthService } from '../service/auth.service';
import { AccountDeletionService } from '../service/account-deletion.service';
import { isPendingDeletionForbidden } from '../model/account-deletion';
import { SESSION_STORE_UNAVAILABLE } from '../model/session-store-unavailable';
import { rehydrateSessionKeys } from '../shared/session-cookie.util';

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {

  /** Timestamp (ms) do último toast de 401 genérico — throttle de 10s, ver warnUnauthorized(). */
  private lastUnauthorizedToastAt = 0;

  /** Timestamp (ms) do último toast de 503 SESSION_STORE_UNAVAILABLE — throttle de 30s. */
  private lastSessionStoreToastAt = 0;

  constructor(
    private accessBlockService: AccessBlockService,
    private authService: AuthService,
    private messageService: MessageService,
    private translocoService: TranslocoService,
    private accountDeletion: AccountDeletionService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const shouldAddHeaders = this.shouldAddHeaders(req.url);

    // Self-heal: se esta é uma chamada autenticada e o sessionStorage perdeu o idToken,
    // mas o localStorage (fonte durável da sessão) ainda o tem, restaura antes de seguir.
    if (shouldAddHeaders && !sessionStorage.getItem('idToken') && localStorage.getItem('idToken')) {
      rehydrateSessionKeys();
    }

    const idToken = sessionStorage.getItem('idToken');

    if (shouldAddHeaders && idToken) {
      const httpOptions = {
        headers: new HttpHeaders({
          // 'Content-Type': 'application/json; charset=utf-8',
          'Authorization': 'Bearer ' + idToken
        })
      };

      // Controle de dispositivos simultâneos (API .NET): precisa ir em toda chamada
      // autenticada, inclusive no /global/refresh — sem ele a API deixa passar
      // sem validar o limite de dispositivos do plano.
      // EXCEÇÃO: o serviço de arquivos (/files/...) é outro backend, com CORS próprio
      // que NÃO permite X-Session-Id (e que tampouco consome esse header). Enviá-lo ali
      // quebra o preflight ("x-session-id is not allowed by Access-Control-Allow-Headers").
      // O Authorization continua indo (é permitido e necessário para o download).
      const sessionId = sessionStorage.getItem('sessionId');
      if (sessionId && !this.isFilesServiceUrl(req.url)) {
        httpOptions.headers = httpOptions.headers.set('X-Session-Id', sessionId);
      }

      // NÃO forçar Content-Type quando o corpo é binário/multipart: File/Blob (upload de
      // parte) e FormData (o browser precisa setar o boundary do multipart/form-data —
      // sobrescrever aqui corrompia o upload, ex.: VCU-identified).
      if (!(req.body instanceof File || req.body instanceof Blob || req.body instanceof FormData)) {
        httpOptions.headers = httpOptions.headers.set('Content-Type', 'application/json; charset=utf-8');
      }

      const clonedRequest = req.clone({
        setHeaders: httpOptions.headers.keys().reduce((headers: Record<string, string>, key: string) => {
          headers[key] = httpOptions.headers.get(key) || '';
          return headers;
        }, {})
      });

      return next.handle(clonedRequest).pipe(catchError(err => this.handleApiError(err, req)));
    }

    return next.handle(req).pipe(catchError(err => this.handleApiError(err, req)));
  }

  /**
   * Reações globais a respostas de erro que qualquer chamada autenticada pode
   * devolver:
   * - 403 approval_pending → bloqueio do shell (waitlist HML).
   * - 403 account.pending_deletion → conta em carência de exclusão: leva à tela de
   *   desistência (ou encerra a sessão, se a conta já foi removida: deletionScheduledAt
   *   nulo). Não confundir com o 403 {code:'self_only'} dos endpoints de exclusão, que cada
   *   tela trata.
   * - 401 SESSION_EXPIRED → sessão não é mais válida no servidor. Um 401 sem esse
   *   código é outro problema e segue o tratamento já existente em cada tela.
   * - 503 SESSION_STORE_UNAVAILABLE → Redis de sessões fora do ar (indisponibilidade
   *   temporária; a sessão pode estar viva). Só avisa por toast: NUNCA desloga nem apaga
   *   tokens. Login/MFA/callback mostram a mensagem deles e o refresh retenta em silêncio.
   *
   * O motivo vem no campo `reason` do corpo (`SessionValidationMiddleware`):
   * - `evicted`  → havia outras sessões ativas e esta não estava entre elas: login em
   *                outro dispositivo (limite do plano) ou logout feito em outro lugar;
   * - `expired`  → nenhuma sessão ativa: TTL/inatividade.
   *
   * `reason` ausente (backend antigo) cai no texto neutro, que não afirma a causa — antes
   * daqui, tudo era tratado como `device_limit` e a tela dizia "acessada em outro
   * dispositivo" mesmo quando era só inatividade.
   */
  private handleApiError(err: unknown, req: HttpRequest<any>): Observable<never> {
    if (err instanceof HttpErrorResponse) {
      const code = err.error?.error;
      if (err.status === 403 && code === 'approval_pending') {
        this.accessBlockService.block();
      } else if (err.status === 403 && isPendingDeletionForbidden(err.error) && !this.isExportUrl(req.url)) {
        if (err.error.deletionScheduledAt === null) {
          void this.authService.logout();
        } else {
          this.accountDeletion.markPending(err.error.deletionScheduledAt);
          void this.accountDeletion.goToPendingPage();
        }
      } else if (err.status === 401 && code === 'SESSION_EXPIRED') {
        const reason = err.error?.reason;
        const sessionError =
          reason === 'evicted' ? 'device_limit' :
          reason === 'expired' ? 'inactivity' :
          'unknown';
        void this.authService.logout({ session_error: sessionError });
      } else if (err.status === 401 && this.shouldWarnUnauthorized(req.url)) {
        this.warnUnauthorized();
      } else if (err.status === 503 && code === SESSION_STORE_UNAVAILABLE && this.shouldWarnSessionStoreUnavailable(req.url)) {
        this.warnSessionStoreUnavailable();
      }
    }
    return throwError(() => err);
  }

  /**
   * A exportação de dados fica liberada na carência (a API não devolve o 403 nessas duas
   * rotas), então a tela de desistência não pode ser tirada de cena por elas.
   */
  private isExportUrl(url: string): boolean {
    return /\/user\/[^/]+\/export(\/status)?(\?|$)/.test(url);
  }

  /**
   * Fallback global: um 401 que não é SESSION_EXPIRED é inesperado — a tela que fez a
   * chamada normalmente não trata esse caso e fica muda pro usuário. Excluídas as rotas
   * com tratamento próprio de falha: /global/refresh (fluxo de refresh já trata) e
   * /user/heartbeat (polling de fundo, falhar quieto é aceitável ali).
   */
  private shouldWarnUnauthorized(url: string): boolean {
    return !url.includes('/global/refresh') && !url.includes('/user/heartbeat');
  }

  /** No máximo 1 toast a cada 10s — uma rajada de chamadas falhando não empilha avisos. */
  private warnUnauthorized(): void {
    const now = Date.now();
    if (now - this.lastUnauthorizedToastAt < 10000) {
      return;
    }
    this.lastUnauthorizedToastAt = now;
    this.messageService.add({
      severity: 'warn',
      summary: this.translocoService.translate('errors.unauthorizedGeneric'),
    });
  }

  /**
   * Rotas com tratamento próprio do 503: as telas de login/MFA/nova senha/callback mostram a
   * mensagem delas, o refresh retenta em silêncio e /user/heartbeat é polling de fundo
   * (falhar quieto é aceitável, como no 401).
   */
  private shouldWarnSessionStoreUnavailable(url: string): boolean {
    const ownHandling = [
      '/global/login',
      '/global/refresh',
      '/global/respond-challenge',
      '/global/new-password-required',
      '/global/oauth/callback',
      '/user/heartbeat',
    ];
    return !ownHandling.some(path => url.includes(path));
  }

  /** No máximo 1 toast a cada 30s — o 503 tende a atingir várias chamadas de uma vez. */
  private warnSessionStoreUnavailable(): void {
    const now = Date.now();
    if (now - this.lastSessionStoreToastAt < 30000) {
      return;
    }
    this.lastSessionStoreToastAt = now;
    this.messageService.add({
      severity: 'warn',
      summary: this.translocoService.translate('errors.sessionStoreUnavailable'),
    });
  }

  /**
   * Rotas do serviço de arquivos (path inicia em /files/...) — backend separado da API
   * .NET, com CORS próprio que não aceita X-Session-Id. Não confundir com o endpoint da
   * API principal /user/{id}/files/{id} (path inicia em /user/), que segue validando sessão.
   */
  private isFilesServiceUrl(url: string): boolean {
    return /^(https?:\/\/[^/]+)?\/files\//i.test(url);
  }

  private shouldAddHeaders(url: string): boolean {
    const urlPatternsToInclude = [
      '/user/',
      '/admin/',
      '/telemetry',
      '/global/',
      '/event/',
      // Chave pública do envelope da senha do mapa (/file/map-password/...): a API exige
      // usuário autenticado. Padrão específico, e não apenas '/file/', porque o serviço de
      // arquivos usa caminhos /file/user/... em outro backend, com CORS próprio.
      '/file/map-password/',
      '/api/',,
      '/tuner/',
      '/tuners',
      '/AI/',
      '/video/'
    ];
    return urlPatternsToInclude.some(pattern => url.includes(pattern || ''));
  }
}
